import { PAYEE_TYPES, type PayeeType, type VendorRow } from "@checkone/contract/client";
import { useId, useState } from "react";
import { useApiMutation } from "../../api/hooks";
import { useToast } from "../../components/Toasts";
import { PAYEE_LABEL } from "../../lib/format";

/**
 * Payee type decides which rate applies (company and AOP rates differ). Until a person confirms it
 * the vendor gets no rate; the shape-based guess is only a preselection (PLAN.md D-17).
 */
export function PayeeTypeCell({ vendor }: { vendor: VendorRow }) {
  const [choice, setChoice] = useState<PayeeType | "">(vendor.payee_type_suggested ?? "");
  const patch = useApiMutation("vendors.patch", ["vendors.list"]);
  const toast = useToast();
  const id = useId();

  if (vendor.payee_type !== "unconfirmed") return <span>{PAYEE_LABEL[vendor.payee_type]}</span>;

  return (
    <form
      className="row"
      style={{ gap: "var(--space-2)", justifyContent: "flex-end", flexWrap: "nowrap" }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!choice) return;
        patch.mutate(
          { params: { id: vendor.id }, body: { payee_type: choice, note: "Payee type confirmed" } },
          {
            onSuccess: () =>
              toast.success(`${vendor.display_name}: payee type confirmed as ${PAYEE_LABEL[choice]}.`),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <label htmlFor={id} className="sr-only">
        Payee type for {vendor.display_name}
      </label>
      <select
        id={id}
        value={choice}
        onChange={(e) => setChoice(e.target.value as PayeeType)}
        style={{ width: "auto", minHeight: 36, minWidth: 0 }}
      >
        <option value="">Choose…</option>
        {PAYEE_TYPES.map((p) => (
          <option key={p} value={p}>
            {PAYEE_LABEL[p]}
            {p === vendor.payee_type_suggested ? " (suggested)" : ""}
          </option>
        ))}
      </select>
      <button className="btn small" type="submit" disabled={!choice || patch.isPending}>
        Confirm
      </button>
    </form>
  );
}
